import { createFileRoute } from '@tanstack/react-router'
import {
  Link,
  useNavigate,
  useTranslation,
  useSelector,
  useDispatch,
  getLocalizedValue,
  type LocalizedText,
} from '@spcc/core'
import { getNextLanguage, isRtlLanguage } from '@spcc/portal-shared'
import { useState, useEffect, useRef } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Button,
  Label,
  Checkbox,
  Eye,
  EyeOff,
  UserPlus,
  Globe,
  Building2,
  Mail,
  User,
  Alert,
  AlertDescription,
  AlertTriangle,
  CheckCircle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  SaudiPhoneInput,
  cn,
  Paperclip,
  X,
  MapPin,
  ChevronRight,
  ChevronLeft,
  FileText,
} from '@spcc/uikit'
import { cleanSaudiPhoneInput, validateSaudiPhone, normalizeSaudiPhoneForStorage } from '@spcc/core'

import {
  authActions,
  selectIsAuthenticated,
  selectIsAuthLoading,
  selectAuthError,
  selectRegistrationMessage,
  selectRegistrationSuccess,
} from '@spcc/domains/auth'
import { SAUDI_REGIONS } from '@spcc/domains/auth'
import { citiesApi } from '@spcc/domains/cities'

export const Route = createFileRoute('/register')({
  component: RegisterPage,
})

// ============================================================================
// External helpers
// ============================================================================

type Translate = ReturnType<typeof useTranslation>['t']

const REGION_FALLBACK_LABELS: Record<string, string> = {
  riyadh: 'Riyadh',
  makkah: 'Makkah',
  madinah: 'Madinah',
  qassim: 'Qassim',
  eastern: 'Eastern Province',
  asir: 'Asir',
  tabuk: 'Tabuk',
  hail: 'Hail',
  northern_borders: 'Northern Borders',
  jazan: 'Jazan',
  najran: 'Najran',
  bahah: 'Al-Bahah',
  jawf: 'Al-Jawf',
}

const REGION_TRANSLATION_KEYS: Record<string, string> = {
  riyadh: 'auth.regionRiyadh',
  makkah: 'auth.regionMakkah',
  madinah: 'auth.regionMadinah',
  qassim: 'auth.regionQassim',
  eastern: 'auth.regionEastern',
  asir: 'auth.regionAsir',
  tabuk: 'auth.regionTabuk',
  hail: 'auth.regionHail',
  northern_borders: 'auth.regionNorthernBorders',
  jazan: 'auth.regionJazan',
  najran: 'auth.regionNajran',
  bahah: 'auth.regionBahah',
  jawf: 'auth.regionJawf',
}

const KDGRP_OPTIONS = ['11', '12', '13'] as const

const KDGRP_TRANSLATION_KEYS: Record<(typeof KDGRP_OPTIONS)[number], string> = {
  '11': 'auth.segmentTransport',
  '12': 'auth.segmentPlants',
  '13': 'auth.segmentBagCustomers',
}

const COUNTRY_OPTIONS = [
  'SA',
  'AE',
  'KW',
  'QA',
  'BH',
  'OM',
  'JO',
  'EG',
  'IQ',
  'YE',
  'US',
  'GB',
  'DE',
  'FR',
  'CN',
  'IN',
  'PK',
  'TR',
  'OTHER',
] as const

const COUNTRY_FALLBACK_LABELS: Record<(typeof COUNTRY_OPTIONS)[number], string> = {
  SA: 'Saudi Arabia',
  AE: 'United Arab Emirates',
  KW: 'Kuwait',
  QA: 'Qatar',
  BH: 'Bahrain',
  OM: 'Oman',
  JO: 'Jordan',
  EG: 'Egypt',
  IQ: 'Iraq',
  YE: 'Yemen',
  US: 'United States',
  GB: 'United Kingdom',
  DE: 'Germany',
  FR: 'France',
  CN: 'China',
  IN: 'India',
  PK: 'Pakistan',
  TR: 'Turkey',
  OTHER: 'Other',
}

interface CityOption {
  id: string
  code: string
  name: LocalizedText
}

async function fetchCities(): Promise<CityOption[]> {
  try {
    const cities = await citiesApi.getCities({ isActive: true, pageSize: 200 })
    return cities.map(city => ({
      id: city.id,
      code: city.code,
      name: city.name,
    }))
  } catch {
    return []
  }
}

function getRegionLabel(region: string, t: Translate): string {
  return t(REGION_TRANSLATION_KEYS[region] ?? '', REGION_FALLBACK_LABELS[region] ?? region)
}

function getCustomerSegmentLabel(value: (typeof KDGRP_OPTIONS)[number], t: Translate): string {
  return t(KDGRP_TRANSLATION_KEYS[value], value)
}

function getCountryLabel(
  countryCode: (typeof COUNTRY_OPTIONS)[number],
  language: string,
  t: Translate
): string {
  if (countryCode === 'OTHER') {
    return t('common.other', COUNTRY_FALLBACK_LABELS.OTHER)
  }

  try {
    const displayNames = new Intl.DisplayNames([language], { type: 'region' })
    return displayNames.of(countryCode) ?? COUNTRY_FALLBACK_LABELS[countryCode]
  } catch {
    return COUNTRY_FALLBACK_LABELS[countryCode]
  }
}

function buildStep1Errors(data: {
  fullName: string
  companyName: string
  email: string
  mobilePhone: string
  telephone: string
  password: string
  confirmPassword: string
  agreeToTerms: boolean
}, t: (key: string) => string): Record<string, string> {
  const errors: Record<string, string> = {}
  if (!data.fullName.trim()) errors.fullName = t('auth.validation.fullNameRequired') || 'Full name is required'
  if (!data.companyName.trim()) errors.companyName = t('auth.validation.companyNameRequired') || 'Company name is required'
  if (!data.email.trim() || !/\S+@\S+\.\S+/.test(data.email)) errors.email = t('auth.validation.emailRequired') || 'Valid email is required'
  const mobileTrimmed = data.mobilePhone.trim()
  if (!mobileTrimmed) {
    errors.mobilePhone = t('validation.phoneRequired') || 'Phone number is required.'
  } else {
    const mobileResult = validateSaudiPhone(mobileTrimmed)
    if (!mobileResult.valid) errors.mobilePhone = t(mobileResult.errorKey ?? 'validation.phoneSaudiFormat') || 'Enter a valid Saudi phone number (9 digits).'
  }
  if (data.telephone.trim()) {
    const telResult = validateSaudiPhone(data.telephone.trim())
    if (!telResult.valid) errors.telephone = t(telResult.errorKey ?? 'validation.phoneSaudiFormat') || 'Enter a valid Saudi phone number (9 digits).'
  }
  if (!data.password || data.password.length < 6) errors.password = t('auth.validation.passwordMin') || 'Password must be at least 6 characters'
  if (data.password !== data.confirmPassword) errors.confirmPassword = t('auth.validation.passwordMismatch') || 'Passwords do not match'
  if (!data.agreeToTerms) errors.agreeToTerms = t('auth.validation.agreeToTermsRequired') || 'You must agree to the terms'
  return errors
}

function buildStep2Errors(data: {
  country: string
  sapCustomerGroup: string
  vatNumber: string
  crNumber: string
  addressLine1: string
  houseNumber: string
  city: string
  region: string
  postalCode: string
  vatCertificate: File | null
  crDocument: File | null
}, t: Translate): Record<string, string> {
  const errors: Record<string, string> = {}
  const isSaudi = data.country === 'SA'
  if (!data.country) errors.country = t('auth.validation.countryRequired', 'Country is required')
  if (!data.sapCustomerGroup) errors.sapCustomerGroup = t('auth.validation.customerSegmentRequired', 'Customer segment is required')
  if (!data.vatNumber.trim()) errors.vatNumber = t('auth.validation.vatNumberRequired', 'VAT number is required')
  if (!data.vatCertificate) errors.vatCertificate = t('auth.validation.vatCertificateRequired', 'VAT certificate file is required')
  if (!data.crNumber.trim()) errors.crNumber = t('auth.validation.crNumberRequired', 'CR number is required')
  if (!data.crDocument) errors.crDocument = t('auth.validation.crDocumentRequired', 'Commercial Registration document is required')
  if (!data.addressLine1.trim()) errors.addressLine1 = t('auth.validation.addressLine1Required', 'Address is required')
  if (data.houseNumber && !/^\d{1,4}$/.test(data.houseNumber)) errors.houseNumber = t('auth.validation.houseNumberInvalid', 'Building number must be up to 4 digits')
  const trimmedPostalCode = data.postalCode.trim()
  if (!trimmedPostalCode) {
    errors.postalCode = t('auth.validation.postalCodeRequired', 'Postal code is required')
  } else if (trimmedPostalCode.length !== 5) {
    errors.postalCode = t('auth.validation.postalCodeLength', 'Postal code must be exactly 5 characters')
  }
  if (isSaudi) {
    if (!data.city) errors.city = t('auth.validation.cityRequired', 'City is required')
    if (!data.region) errors.region = t('auth.validation.regionRequired', 'Region is required')
  }
  return errors
}

function FilePickerRow({
  label,
  hint,
  file,
  onPick,
  onClear,
  disabled,
  error,
  'data-testid': testId,
}: {
  label: string
  hint: string
  file: File | null
  onPick: (f: File) => void
  onClear: () => void
  disabled: boolean
  error?: string
  'data-testid'?: string
}) {
  const ref = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <Label>{label}</Label>
      <p className="text-xs text-muted-foreground">{hint}</p>
      {file ? (
        <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-background/50 px-3 py-2">
          <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="flex-1 text-sm truncate">{file.name}</span>
          <Badge variant="secondary" className="text-xs shrink-0">{(file.size / 1024).toFixed(0)} KB</Badge>
          {!disabled && (
            <Button type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={onClear}>
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      ) : (
        <Button type="button" variant="outline" className="w-full" onClick={() => ref.current?.click()} disabled={disabled}>
          <Paperclip className="h-4 w-4 me-2" />
          {t('auth.chooseFile', 'Choose file')}
        </Button>
      )}
      <input
        ref={ref}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) onPick(f) }}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

// ============================================================================
// RegisterPage
// ============================================================================

function RegisterPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isLoading = useSelector(selectIsAuthLoading)
  const authError = useSelector(selectAuthError)
  const registrationSuccess = useSelector(selectRegistrationSuccess)
  const registrationMessage = useSelector(selectRegistrationMessage)

  const isRTL = isRtlLanguage(i18n.language)
  const isFormDisabled = isLoading || registrationSuccess
  const countryOptions = COUNTRY_OPTIONS.map(value => ({
    value,
    label: getCountryLabel(value, i18n.language, t),
  }))
  const customerSegmentOptions = KDGRP_OPTIONS.map(value => ({
    value,
    label: getCustomerSegmentLabel(value, t),
  }))

  // Step state
  const [step, setStep] = useState<1 | 2>(1)

  // Step 1 state
  const [fullName, setFullName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [email, setEmail] = useState('')
  const [mobilePhone, setMobilePhone] = useState('')
  const [telephone, setTelephone] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [agreeToTerms, setAgreeToTerms] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Step 2 state
  const [country, setCountry] = useState('SA')
  const [sapCustomerGroup, setSapCustomerGroup] = useState('')
  const [vatNumber, setVatNumber] = useState('')
  const [vatCertificate, setVatCertificate] = useState<File | null>(null)
  const [crNumber, setCrNumber] = useState('')
  const [crDocument, setCrDocument] = useState<File | null>(null)
  const [addressLine1, setAddressLine1] = useState('')
  const [houseNumber, setHouseNumber] = useState('')
  const [city, setCity] = useState('')
  const [region, setRegion] = useState('')
  const [postalCode, setPostalCode] = useState('')

  // Cities
  const [cities, setCities] = useState<CityOption[]>([])

  const isSaudi = country === 'SA'

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const toggleLanguage = () => {
    const next = getNextLanguage(i18n.language)
    i18n.changeLanguage(next)
  }

  useEffect(() => {
    if (isAuthenticated) navigate({ to: '/' })
  }, [isAuthenticated, navigate])

  useEffect(() => {
    dispatch(authActions.clearError())
    dispatch(authActions.resetRegistration())
  }, [dispatch])

  useEffect(() => {
    if (isSaudi) {
      fetchCities().then(setCities)
    } else {
      setCities([])
      setCity('')
      setRegion('')
    }
  }, [isSaudi])

  const handleNextStep = () => {
    const errors = buildStep1Errors({ fullName, companyName, email, mobilePhone, telephone, password, confirmPassword, agreeToTerms }, t)
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    setFieldErrors({})
    setStep(2)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const errors = buildStep2Errors({ country, sapCustomerGroup, vatNumber, crNumber, addressLine1, houseNumber, city, region, postalCode, vatCertificate, crDocument }, t)
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    setFieldErrors({})

    const normalizedEmail = email.trim().toLowerCase()
    const normalizedMobile = normalizeSaudiPhoneForStorage(mobilePhone.trim()) ?? mobilePhone.trim()
    const normalizedTelephone = telephone.trim() ? (normalizeSaudiPhoneForStorage(telephone.trim()) ?? telephone.trim()) : undefined
    dispatch(authActions.register({
      username: normalizedEmail,
      email: normalizedEmail,
      password,
      fullName: fullName.trim(),
      companyName: companyName.trim(),
      mobilePhone: normalizedMobile,
      telephone: normalizedTelephone,
      vatNumber: vatNumber.trim(),
      crNumber: crNumber.trim(),
      addressLine1: addressLine1.trim(),
      houseNumber: houseNumber.trim() || undefined,
      city: isSaudi ? city : undefined,
      region: isSaudi ? region : undefined,
      postalCode: postalCode.trim(),
      vatCertificate: vatCertificate!,
      crDocument: crDocument!,
      country,
      sapCustomerGroup,
    }))
  }

  const FieldError = ({ field }: { field: string }) =>
    fieldErrors[field] ? (
      <p className="text-xs text-destructive mt-1">{fieldErrors[field]}</p>
    ) : null

  return (
    <div
      className="safe-top relative min-h-screen flex items-center justify-center overflow-hidden py-8"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary/30 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-accent/20 via-transparent to-transparent rounded-full blur-3xl" />
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="absolute top-[calc(env(safe-area-inset-top)+1rem)] end-4 glass"
        onClick={toggleLanguage}
      >
        <Globe className="h-5 w-5" />
      </Button>

      <div className="w-full max-w-lg px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <span className="text-2xl font-bold text-primary">SPCC</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">{t('auth.createAccount')}</h1>
          <p className="text-muted-foreground mt-1">{t('auth.registerSubtitle')}</p>
        </div>

        <Card className="glass-card border-white/10">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">
                {registrationSuccess
                  ? t('auth.registrationSuccessful')
                  : step === 1
                  ? t('auth.accountInfo', 'Account Info')
                  : t('auth.companyDetails', 'Company Details')}
              </CardTitle>
              {!registrationSuccess && (
                <div className="flex items-center gap-1.5">
                  <div className={cn('h-2 w-8 rounded-full', step === 1 ? 'bg-primary' : 'bg-muted')} />
                  <div className={cn('h-2 w-8 rounded-full', step === 2 ? 'bg-primary' : 'bg-muted')} />
                </div>
              )}
            </div>
            <CardDescription>
              {registrationSuccess
                ? t('auth.registrationSuccessMessage')
                : step === 1
                ? t('auth.fillDetails')
                : t('auth.companyDetailsSubtitle', 'Fill in your company and address details')}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {registrationSuccess ? (
              <div data-testid="register-success" className="flex flex-col items-center justify-center py-6 space-y-4">
                <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <div className="text-center space-y-2">
                  <h3 className="text-lg font-medium">{t('auth.registrationSuccessful')}</h3>
                  <p className="text-muted-foreground max-w-xs mx-auto">
                    {registrationMessage
                      ? t(registrationMessage, { defaultValue: registrationMessage })
                      : t('auth.registrationPendingMessage')}
                  </p>
                </div>
                <Button className="w-full mt-4" onClick={() => navigate({ to: '/login' })}>
                  {t('auth.buttons.backToLogin')}
                </Button>
              </div>
            ) : step === 1 ? (
              /* ── STEP 1 ─────────────────────────────────────────────── */
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">{t('auth.fullName')}</Label>
                    <div className="relative">
                      <User className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="fullName" data-testid="register-fullname" placeholder={t('auth.fullNamePlaceholder')} value={fullName} onChange={e => setFullName(e.target.value)} disabled={isFormDisabled} className="bg-background/50 ps-10" />
                    </div>
                    <FieldError field="fullName" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyName">{t('auth.companyName')}</Label>
                    <div className="relative">
                      <Building2 className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="companyName" data-testid="register-company" placeholder={t('auth.companyNamePlaceholder')} value={companyName} onChange={e => setCompanyName(e.target.value)} disabled={isFormDisabled} className="bg-background/50 ps-10" />
                    </div>
                    <FieldError field="companyName" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input id="email" data-testid="register-email" type="email" placeholder={t('auth.emailPlaceholder')} value={email} onChange={e => setEmail(e.target.value)} disabled={isFormDisabled} className="bg-background/50 ps-10" />
                  </div>
                  <FieldError field="email" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="mobilePhone">{t('auth.mobilePhone', 'Mobile Phone')} *</Label>
                    <SaudiPhoneInput
                      id="mobilePhone"
                      data-testid="register-mobile"
                      value={cleanSaudiPhoneInput(mobilePhone)}
                      onChange={digits => setMobilePhone(digits)}
                      placeholder="512345678"
                      disabled={isFormDisabled}
                      className={cn('bg-background/50', fieldErrors.mobilePhone && 'border-destructive')}
                      error={fieldErrors.mobilePhone}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telephone">
                      {t('auth.telephone', 'Telephone')}{' '}
                      <span className="text-muted-foreground text-xs">({t('common.optional', 'optional')})</span>
                    </Label>
                    <SaudiPhoneInput
                      id="telephone"
                      data-testid="register-telephone"
                      value={cleanSaudiPhoneInput(telephone)}
                      onChange={digits => setTelephone(digits)}
                      placeholder="112345678"
                      disabled={isFormDisabled}
                      className={cn('bg-background/50', fieldErrors.telephone && 'border-destructive')}
                      error={fieldErrors.telephone}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">{t('auth.password')}</Label>
                    <div className="relative">
                      <Input id="password" data-testid="register-password" type={showPassword ? 'text' : 'password'} placeholder={t('auth.passwordPlaceholder')} value={password} onChange={e => setPassword(e.target.value)} disabled={isFormDisabled} className="bg-background/50 pe-10" />
                      <Button type="button" variant="ghost" size="icon" className="absolute end-0 top-0 h-full px-3 hover:bg-transparent" onClick={() => setShowPassword(v => !v)} disabled={isFormDisabled}>
                        {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                      </Button>
                    </div>
                    <FieldError field="password" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
                    <div className="relative">
                      <Input id="confirmPassword" data-testid="register-confirm-password" type={showConfirmPassword ? 'text' : 'password'} placeholder={t('auth.confirmPasswordPlaceholder')} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} disabled={isFormDisabled} className="bg-background/50 pe-10" />
                      <Button type="button" variant="ghost" size="icon" className="absolute end-0 top-0 h-full px-3 hover:bg-transparent" onClick={() => setShowConfirmPassword(v => !v)} disabled={isFormDisabled}>
                        {showConfirmPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                      </Button>
                    </div>
                    <FieldError field="confirmPassword" />
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Checkbox id="terms" data-testid="register-terms" checked={agreeToTerms} onCheckedChange={v => setAgreeToTerms(v as boolean)} disabled={isFormDisabled} className="mt-0.5" />
                  <Label htmlFor="terms" className="text-sm font-normal leading-relaxed">
                    {t('auth.agreeToTerms')}{' '}
                    <Link to="/privacy-policy" className="text-primary hover:underline">{t('auth.privacyPolicy')}</Link>
                  </Label>
                </div>
                <FieldError field="agreeToTerms" />

                <Button type="button" data-testid="register-next" className="w-full" onClick={handleNextStep} disabled={isFormDisabled}>
                  {t('auth.nextStep', 'Next')}
                  <ChevronRight className="h-4 w-4 ms-1" />
                </Button>

                <div className="text-center text-sm">
                  <span className="text-muted-foreground">{t('auth.alreadyHaveAccount')}{' '}</span>
                  <Link to="/login" className="text-primary hover:underline">{t('auth.signIn')}</Link>
                </div>
              </div>
            ) : (
              /* ── STEP 2 ─────────────────────────────────────────────── */
              <form onSubmit={handleSubmit} className="space-y-5">

                {/* ── VAT Section ── */}
                <div className="rounded-xl border border-border/50 bg-background/30 p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('auth.vatSection', 'VAT Information')}
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="vatNumber">{t('auth.vatNumber', 'VAT Number')}</Label>
                    <div className="relative">
                      <FileText className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="vatNumber" data-testid="register-vat-number" placeholder="3XXXXXXXXXX3" value={vatNumber} onChange={e => setVatNumber(e.target.value)} disabled={isFormDisabled} className="bg-background/50 ps-10" />
                    </div>
                    <FieldError field="vatNumber" />
                  </div>
                  <FilePickerRow
                    label={t('auth.vatCertificate', 'VAT Certificate')}
                    hint={t('auth.vatCertificateHint', 'Upload your official VAT certificate (PDF or image)')}
                    file={vatCertificate}
                    onPick={setVatCertificate}
                    onClear={() => setVatCertificate(null)}
                    disabled={isFormDisabled}
                    error={fieldErrors.vatCertificate}
                    data-testid="register-vat-cert"
                  />
                </div>

                {/* ── CR Section ── */}
                <div className="rounded-xl border border-border/50 bg-background/30 p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('auth.crSection', 'Commercial Registration')}
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="crNumber">{t('auth.crNumber', 'CR Number')}</Label>
                    <div className="relative">
                      <FileText className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="crNumber" data-testid="register-cr-number" placeholder="1010XXXXXX" value={crNumber} onChange={e => setCrNumber(e.target.value)} disabled={isFormDisabled} className="bg-background/50 ps-10" />
                    </div>
                    <FieldError field="crNumber" />
                  </div>
                  <FilePickerRow
                    label={t('auth.crDocument', 'CR Document')}
                    hint={t('auth.crDocumentHint', 'Upload your Commercial Registration certificate (PDF or image)')}
                    file={crDocument}
                    onPick={setCrDocument}
                    onClear={() => setCrDocument(null)}
                    disabled={isFormDisabled}
                    error={fieldErrors.crDocument}
                    data-testid="register-cr-doc"
                  />
                </div>

                {/* ── Address Section ── */}
                <div className="rounded-xl border border-border/50 bg-background/30 p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('auth.addressSection', 'Address')}
                  </p>

                  {/* Country */}
                  <div className="space-y-2">
                    <Label>{t('auth.country', 'Country')} *</Label>
                    <Select value={country} onValueChange={setCountry} disabled={isFormDisabled}>
                      <SelectTrigger data-testid="register-country" className="bg-background/50">
                        <SelectValue placeholder={t('auth.selectCountry', 'Select country')} />
                      </SelectTrigger>
                      <SelectContent>
                        {countryOptions.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FieldError field="country" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="addressLine1">{t('auth.addressLine1', 'Street Address')} *</Label>
                    <div className="relative">
                      <MapPin className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="addressLine1" data-testid="register-street" placeholder={t('auth.addressLine1Placeholder', 'Street name')} value={addressLine1} onChange={e => setAddressLine1(e.target.value)} disabled={isFormDisabled} className="bg-background/50 ps-10" />
                    </div>
                    <FieldError field="addressLine1" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="houseNumber">
                        {t('auth.houseNumber', 'Building No.')}{' '}
                        <span className="text-muted-foreground text-xs">({t('common.optional', 'optional')})</span>
                      </Label>
                      <Input id="houseNumber" data-testid="register-building" placeholder="1234" maxLength={4} value={houseNumber} onChange={e => setHouseNumber(e.target.value.replace(/\D/g, '').slice(0, 4))} disabled={isFormDisabled} className="bg-background/50" />
                      <FieldError field="houseNumber" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="postalCode">{t('auth.postalCode', 'Postal Code')} *</Label>
                      <Input id="postalCode" data-testid="register-postal-code" placeholder="12345" maxLength={5} value={postalCode} onChange={e => setPostalCode(e.target.value)} disabled={isFormDisabled} className="bg-background/50" />
                      <FieldError field="postalCode" />
                    </div>
                  </div>

                  {isSaudi && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('auth.city', 'City')} *</Label>
                        <Select value={city} onValueChange={setCity} disabled={isFormDisabled}>
                          <SelectTrigger data-testid="register-city" className="bg-background/50">
                            <SelectValue placeholder={cities.length === 0 ? '...' : t('auth.selectCity', 'Select city')} />
                          </SelectTrigger>
                          <SelectContent>
                            {cities.map(c => (
                              <SelectItem key={c.id} value={c.name.en}>
                                {getLocalizedValue(c.name, i18n.language)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FieldError field="city" />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('auth.region', 'Region')} *</Label>
                        <Select value={region} onValueChange={setRegion} disabled={isFormDisabled}>
                          <SelectTrigger data-testid="register-region" className="bg-background/50">
                            <SelectValue placeholder={t('auth.selectRegion', 'Select region')} />
                          </SelectTrigger>
                          <SelectContent>
                            {SAUDI_REGIONS.map(r => (
                              <SelectItem key={r} value={r}>{getRegionLabel(r, t)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FieldError field="region" />
                      </div>
                    </div>
                  )}
                </div>{/* end address section */}

                {/* ── Business Segment Section ── */}
                <div className="rounded-xl border border-border/50 bg-background/30 p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('auth.businessSegment', 'Business Segment')}
                  </p>
                  <div className="space-y-2">
                    <Label>{t('auth.customerSegment', 'Customer Segment')} *</Label>
                    <p className="text-xs text-muted-foreground">
                      {t('auth.customerSegmentHint', 'Select the category that best describes your business type.')}
                    </p>
                    <Select value={sapCustomerGroup} onValueChange={setSapCustomerGroup} disabled={isFormDisabled}>
                      <SelectTrigger data-testid="register-segment" className="bg-background/50">
                        <SelectValue placeholder={t('auth.selectSegment', 'Select segment')} />
                      </SelectTrigger>
                      <SelectContent>
                        {customerSegmentOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FieldError field="sapCustomerGroup" />
                  </div>
                </div>{/* end business segment section */}

                {authError && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{t(authError, { defaultValue: authError })}</AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-3">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => { setStep(1); setFieldErrors({}) }} disabled={isFormDisabled}>
                    <ChevronLeft className="h-4 w-4 me-1" />
                    {t('auth.back', 'Back')}
                  </Button>
                  <Button type="submit" data-testid="register-submit" className="flex-1" disabled={isFormDisabled}>
                    {isLoading ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4 me-2" />
                        {t('auth.createAccount')}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
